import {
	ContentBlogUnifiedQueryArtifact,
	ContentUnifiedQueryArtifact,
	ContentUnifiedWithSpaceKeyQueryArtifact,
	createContentBlogUnifiedQueryVariables,
	createContentUnifiedQueryVariables,
	createContentUnifiedWithSpaceKeyQueryVariables,
	type ContentBlogUnifiedQueryType,
	type ContentBlogUnifiedQueryVariablesType,
	type ContentUnifiedQueryType,
	type ContentUnifiedQueryVariablesType,
	type ContentUnifiedWithSpaceKeyQueryType,
	type ContentUnifiedWithSpaceKeyQueryVariablesType,
} from '@confluence/content-unified-query';
import { fg } from '@confluence/feature-gating';
import { isUnauthorizedError } from '@confluence/query-preloader-tools/entry-points/isUnauthorizedError.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { ssrPermissionsErrorHandler } from '@confluence/ssr-utilities/entry-points/SSRPermissionsErrorHandler.ts';

type PreloadContentParameters = {
	contentId: string;
	isBlog: boolean;
	spaceKey: string;
	shouldPreloadUsingSpaceKey?: boolean;
	versionOverride?: number | null;
	userId?: string | null;
	agentSessionsEnabled?: boolean;
	hasDocumentContractAnalyticsByline?: boolean;
	hasDocumentContractContentHeader?: boolean;
};

type PreloadContentResult = {
	result?: any;
	hasErrors: boolean;
};

type ChooseContentUnifiedQueryVariablesType =
	| ContentUnifiedQueryVariablesType
	| ContentBlogUnifiedQueryVariablesType;

function processContentPreloadPermissionError(result: any) {
	result?.errors?.forEach((error: any) => {
		// The "Cannot find space" condition is handled by redirecting to the correct space
		// even if error is not propagated to ContentPrerequisites where it is normally handled.
		if (isUnauthorizedError(error)) {
			ssrPermissionsErrorHandler.markSSRPreloadPermissionError('view', error);
		}
	});
}

export const transformContentData = (
	data: any,
): {
	content: {
		nodes: any[];
		// @ts-ignore
		__typename: string;
	} | null;
	space: any;
} => ({
	content: data?.spaceHomepage
		? {
				nodes: [data?.spaceHomepage],
				// @ts-ignore
				__typename: 'PaginatedContentListWithChild',
			}
		: null,
	space: data?.space || null,
});

export function preloadContent({
	contentId,
	spaceKey,
	isBlog,
	versionOverride = null,
	userId,
	agentSessionsEnabled = false,
	hasDocumentContractAnalyticsByline = false,
	hasDocumentContractContentHeader = false,
}: PreloadContentParameters): Promise<PreloadContentResult> {
	window.performance.mark('CFP-63.preloadContent.start');
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR);
	const shouldUseContentHeaderFragment =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		hasDocumentContractContentHeader &&
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		fg('confluence_vp_header_frag_reader');

	// Space overview
	if (spaceKey && !contentId) {
		return query<ContentUnifiedWithSpaceKeyQueryType, ContentUnifiedWithSpaceKeyQueryVariablesType>(
			{
				query: ContentUnifiedWithSpaceKeyQueryArtifact,
				variables: {
					...createContentUnifiedWithSpaceKeyQueryVariables({
						spaceKey,
						versionOverride,
						isSSR,
						agentSessionsUserId: agentSessionsEnabled ? userId : undefined,
						includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
						includeMetadata: fg('confluence_vp_meta_frag_reader'),
						includeClassification: fg('confluence_vp_classification_context_frag_reader'),
						includeReactions: fg('confluence_vp_reactions_frag_reader'),
					}),
					...(fg('confluence_vp_mode_frag_reader') ? { useContentModeFragment: true } : {}),
				},
				errorPolicy: 'all',
			},
		).then((result: any) => {
			// Record the end mark and timestamp to track *both* duration of preloading
			// and timestamp of the moment when preloading finished
			window.performance.mark('CFP-63.preloadContent.end');
			window.performance.mark('CFP-63.preloadContentEnd');

			const data = result?.data;
			const homepageId = data?.spaceHomepage?.id;
			if (homepageId) {
				// update result with transformed data
				result.data = transformContentData(data);

				writeQueryInterop<ContentUnifiedQueryType, ContentUnifiedQueryVariablesType>({
					query: ContentUnifiedQueryArtifact,
					variables: {
						...createContentUnifiedQueryVariables({
							contentId: homepageId,
							spaceKey,
							versionOverride,
							isSSR,
							agentSessionsUserId: agentSessionsEnabled ? userId : undefined,
							includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
							includeMetadata: fg('confluence_vp_meta_frag_reader'),
							includeClassification: fg('confluence_vp_classification_context_frag_reader'),
							includeReactions: fg('confluence_vp_reactions_frag_reader'),
						}),
						...(fg('confluence_vp_mode_frag_reader') ? { useContentModeFragment: true } : {}),
					},
					data: { ...result.data },
				});
			}

			processContentPreloadPermissionError(result);

			return { result, hasErrors: result?.hasErrors };
		});
	}

	const chooseContentUnifiedQuery = isBlog
		? ContentBlogUnifiedQueryArtifact
		: ContentUnifiedQueryArtifact;
	const queryVariables: ChooseContentUnifiedQueryVariablesType = isBlog
		? {
				...createContentBlogUnifiedQueryVariables({
					contentId,
					spaceKey,
					versionOverride,
					isSSR,
					preloadLabels: isSSR,
					includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
					includeBylineContributors: fg('confluence_vp_title_byline_frag_dedup'),
					includeAutomationDiscoveries: fg('cc_automations_discovery_nudges'),
					includeMetadata: fg('confluence_vp_meta_frag_reader'),
					includeClassification: fg('confluence_vp_classification_context_frag_reader'),
					includeReactions: fg('confluence_vp_reactions_frag_reader'),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeAnalyticsByline:
						hasDocumentContractAnalyticsByline && fg('confluence_vp_analytics_byline_frag_reader'),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeContentHeader: shouldUseContentHeaderFragment,
					includeContentHeaderAccess:
						shouldUseContentHeaderFragment && fg('confluence_vp_access_frag_reader'),
					includeContentHeaderPublicLink:
						shouldUseContentHeaderFragment && !isBlog && fg('confluence_vp_access_frag_reader'),
				}),
				...(fg('confluence_vp_mode_frag_reader') ? { useContentModeFragment: true } : {}),
			}
		: {
				...createContentUnifiedQueryVariables({
					contentId,
					spaceKey,
					versionOverride,
					isSSR,
					preloadLabels: isSSR,
					agentSessionsUserId: agentSessionsEnabled ? userId : undefined,
					includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
					includeBylineContributors: fg('confluence_vp_title_byline_frag_dedup'),
					includeAutomationDiscoveries: fg('cc_automations_discovery_nudges'),
					includeMetadata: fg('confluence_vp_meta_frag_reader'),
					includeClassification: fg('confluence_vp_classification_context_frag_reader'),
					includeReactions: fg('confluence_vp_reactions_frag_reader'),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeAnalyticsByline:
						hasDocumentContractAnalyticsByline && fg('confluence_vp_analytics_byline_frag_reader'),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeContentHeader: shouldUseContentHeaderFragment,
					includeContentHeaderAccess:
						shouldUseContentHeaderFragment && fg('confluence_vp_access_frag_reader'),
					includeContentHeaderPublicLink:
						shouldUseContentHeaderFragment && !isBlog && fg('confluence_vp_access_frag_reader'),
				}),
				...(fg('confluence_vp_mode_frag_reader') ? { useContentModeFragment: true } : {}),
			};

	return query<
		ContentUnifiedQueryType | ContentBlogUnifiedQueryType,
		ChooseContentUnifiedQueryVariablesType
	>({
		query: chooseContentUnifiedQuery,
		variables: queryVariables,
		errorPolicy: 'all',
	}).then((result: any) => {
		// Record the end mark and timestamp to track *both* duration of preloading
		// and timestamp of the moment when preloading finished
		window.performance.mark('CFP-63.preloadContent.end');
		window.performance.mark('CFP-63.preloadContentEnd');

		processContentPreloadPermissionError(result);

		return { result, hasErrors: result?.hasErrors };
	});
}
